// A community as the primary-community picker needs it. `isActive` is kept so an
// inactive community can still be listed - a contact may already hold one as
// their primary - while being marked as what it is.
export type CommunityOption = {
  id: number
  name: string
  isActive: boolean
}

export type ChangeDefaultCommunityResult =
  | { ok: true; defaultCommunityId: number }
  | { ok: false; code?: string; status?: number; message?: string }

/**
 * The admin community list, as options sorted by name. Rows without an id or a
 * name are dropped: they cannot be picked and would render as a blank line.
 */
export const toCommunityOptions = (communities: any): CommunityOption[] =>
  (Array.isArray(communities) ? communities : [])
    .filter(community => community?.id != null && community?.name)
    .map(community => ({
      id: Number(community.id),
      name: String(community.name),
      // The community form reads the same field as its enabled switch
      // (status === 1), so an absent status counts as active rather than
      // marking every community inactive.
      isActive: community.status == null || Number(community.status) === 1,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

/**
 * What the change request came back with. The procedure reports failures both
 * ways - an HTTP status for the ones the controller maps (USER_NOT_FOUND,
 * COMMUNITY_NOT_FOUND, RELATION_CREATE_FAILED...) and a success flag inside a
 * 200 body - so both are read here. `requestedCommunityId` is the fallback for
 * the new primary community: it is what was asked for, and the profile the
 * server returns alongside is only there to confirm it.
 */
export const readChangeResult = (res: any, requestedCommunityId: number): ChangeDefaultCommunityResult => {
  if (!res) {
    return { ok: false }
  }

  const status = res.status ?? res.statusCode
  if (typeof status === 'number' && status >= 400) {
    return {
      ok: false,
      status,
      code: res.data?.errorCode,
      message: res.data?.errorMessage || res.errorDescription,
    }
  }

  const spResult = res.spResult ?? res
  if (spResult?.success === false) {
    return { ok: false, code: spResult.errorCode, message: spResult.errorMessage }
  }

  const profileDefault = res.profile?.user?.defaultCommunityID ?? res.profile?.user?.defaultCommunityId

  return { ok: true, defaultCommunityId: Number(profileDefault ?? requestedCommunityId) }
}

/**
 * The communities a contact's primary community may be moved to: the ones they
 * already belong to. Their current primary community is always kept, even when
 * the membership list does not mention it, so the picker can show where they
 * are now rather than opening on an empty field.
 */
export const limitToMemberships = (
  options: CommunityOption[],
  memberCommunityIds: (number | string)[] | null | undefined,
  defaultCommunityId: number | string | null | undefined,
): CommunityOption[] => {
  const allowed = new Set(
    (Array.isArray(memberCommunityIds) ? memberCommunityIds : [])
      .filter(id => id != null)
      .map(id => Number(id)),
  )

  if (defaultCommunityId != null) {
    allowed.add(Number(defaultCommunityId))
  }

  return options.filter(option => allowed.has(Number(option.id)))
}
